/**
 * salonDurumlari.jsx — salon hizmet durumları (backlog #194) ve kroki çizim durumları (#202)
 *
 * Backend karşılıkları (FDS-Backend, V53__26_9_salon_cizim_durumu_hizmet_fuar_alani.sql):
 *   hizmet   salon_hizmet.durum — SalonHizmetDurumConstant: 0 yok / 1 var / 2 harici destek;
 *            satır yoksa BİLİNMİYOR (API'de durum: null). Fuar alanındaki salona ait.
 *   çizim    CizimDurumuConstant: 0 çizim yok / 1 çiziliyor / 2 tamamlandı —
 *            Salon Planı + Altyapı Planı fuar alanındaki salona (fuar_alani_salon),
 *            Alan Planı fuardaki salona (salon_to_fuar) aittir.
 *
 * Bu dosya renkleri/etiketleri TEK yerde tutar: FuarAlaniSalonlariCard, SalonlarPage, salon
 * kartı ve plan düzenleme sayfası aynı dili konuşsun.
 *
 * #291 (2026-10-08): hizmetler de plan durumları gibi menüden seçiliyor — HizmetDurumuPill.
 */
import React from "react";
import {
  Badge, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip,
} from "reactstrap";
import { CircleDashed, PencilLine, CircleCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

// ── Hizmetler (#194) ───────────────────────────────────────────────────────────────

export const HIZMET_DURUM = { YOK: 0, VAR: 1, HARICI_DESTEK: 2 };

/** durum → { label, color, className } — null/undefined = Bilinmiyor. */
export function hizmetDurumMeta(durum) {
  switch (durum) {
    case HIZMET_DURUM.VAR: return { label: i18n.t("components:salonDurumlari.var"), color: "success", className: "" };
    case HIZMET_DURUM.YOK: return { label: i18n.t("components:salonDurumlari.yok"), color: "secondary", className: "" };
    case HIZMET_DURUM.HARICI_DESTEK: return { label: i18n.t("components:salonDurumlari.hariciDestek"), color: "warning", className: "" };
    default: return { label: i18n.t("components:salonDurumlari.bilinmiyor"), color: "light", className: "text-muted border" };
  }
}

/** Tıklama sırası: Bilinmiyor → Var → Yok → Harici Destek → Bilinmiyor. */
export function nextHizmetDurum(durum) {
  if (durum == null) return HIZMET_DURUM.VAR;
  if (durum === HIZMET_DURUM.VAR) return HIZMET_DURUM.YOK;
  if (durum === HIZMET_DURUM.YOK) return HIZMET_DURUM.HARICI_DESTEK;
  return null;
}

/** API satırındaki bir hizmetin durumu — pre-#194 yanıtlarda yalnızca `mevcut` vardı. */
export function hizmetDurumOf(h) {
  if (!h) return null;
  if (h.durum !== undefined) return h.durum;
  if (h.mevcut === true) return HIZMET_DURUM.VAR;
  if (h.mevcut === false) return HIZMET_DURUM.YOK;
  return null;
}

export const HIZMET_LEGEND = [
  HIZMET_DURUM.VAR, HIZMET_DURUM.YOK, HIZMET_DURUM.HARICI_DESTEK, null,
].map((d) => ({ durum: d, ...hizmetDurumMeta(d) }));

/**
 * Bir hizmetin durum rozeti (#291, 2026-10-08). `onChange` verilirse tıklanınca dört seçenekli bir
 * menü açar (sıra lejantla aynı: Var / Yok / Harici Destek / Bilinmiyor); verilmezse ya da
 * `disabled` ise salt okunur rozet. WAS (#194): her tıklama nextHizmetDurum ile sıradaki duruma
 * geçiyordu, istenen duruma tıklaya tıklaya varılıyordu.
 * Seçim yalnızca `onChange`'e iletilir — kaydetmek çağıranın işi: Salonlar tablosu ve Salon Kartı
 * taslağa yazar, satırın / kartın Kaydet'i gönderir (Bilge, 2026-10-08: plan durumları gibi hemen
 * yazılmıyor). `showLabel` rozete "Elektrik: Var" yazısını ekler; yoksa yalnızca ikon.
 * `id` tooltip hedefi olarak sayfada benzersiz olmalı.
 */
export function HizmetDurumuPill({
  id, ad, Icon = null, iconSize = 11, durum, onChange, disabled = false, showLabel = false,
  className = "", style = null,
}) {
  const { t } = useTranslation("components");
  const current = durum ?? null;
  const meta = hizmetDurumMeta(current);
  const badge = (
    <Badge
      id={id}
      color={meta.color}
      pill
      className={`d-inline-flex align-items-center gap-1 ${meta.className} ${className}`}
      style={{ cursor: onChange && !disabled ? "pointer" : "default", ...style }}
    >
      {Icon && <Icon size={iconSize} />}
      {showLabel && <span>{ad}: {meta.label}</span>}
    </Badge>
  );
  const tip = <UncontrolledTooltip target={id} placement="top">{ad}: {meta.label}</UncontrolledTooltip>;

  if (!onChange || disabled) {
    return <>{badge}{tip}</>;
  }
  return (
    <UncontrolledDropdown className="d-inline-block">
      <DropdownToggle tag="span" data-toggle="dropdown">{badge}</DropdownToggle>
      <DropdownMenu>
        <DropdownItem header>{ad}{" "}{t("salonDurumlari.hizmetDurumu")}</DropdownItem>
        {HIZMET_LEGEND.map((o) => (
          <DropdownItem
            key={o.label}
            active={o.durum === current}
            onClick={() => { if (o.durum !== current) onChange(o.durum); }}
          >
            <Badge
              color={o.color} pill className={`me-2 p-0 ${o.className}`}
              style={{ width: 10, height: 10, verticalAlign: "middle" }}
            >
              {" "}
            </Badge>
            {o.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
      {tip}
    </UncontrolledDropdown>
  );
}

// ── Çizim durumları (#202) ─────────────────────────────────────────────────────────

export const CIZIM_DURUM = { CIZIM_YOK: 0, CIZILIYOR: 1, TAMAMLANDI: 2 };

export const CIZIM_DURUM_SECENEKLERI = [
  { durum: CIZIM_DURUM.CIZIM_YOK, get label() { return i18n.t("components:salonDurumlari.cizimYok"); }, color: "light", className: "text-muted border", Icon: CircleDashed },
  { durum: CIZIM_DURUM.CIZILIYOR, get label() { return i18n.t("components:salonDurumlari.ciziliyor"); }, color: "warning", className: "", Icon: PencilLine },
  { durum: CIZIM_DURUM.TAMAMLANDI, get label() { return i18n.t("components:salonDurumlari.tamamlandi"); }, color: "success", className: "", Icon: CircleCheck },
];

/** null = durum bilinmiyor (ör. salon fuar alanında kayıtlı değil). */
export function cizimDurumMeta(durum) {
  return CIZIM_DURUM_SECENEKLERI.find((o) => o.durum === durum)
    ?? { durum: null, label: "—", color: "light", className: "text-muted border", Icon: CircleDashed };
}

export const PLAN_ADLARI = {
  get salonPlani() { return i18n.t("components:salonDurumlari.salonPlani"); },
  get altyapiPlani() { return i18n.t("components:salonDurumlari.altyapiPlani"); },
  get alanPlani() { return i18n.t("components:salonDurumlari.alanPlani"); },
};

const fmtTarih = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(currentLocaleTag());
};

// ── Alan Planı kapısı (#235 / #236) ────────────────────────────────────────────────

export const ALAN_PLANI_ENGEL_KISA = "Önce Salon Planı tamamlanmalı";

/** Salon Planı tamamlanmadan Alan Planı'nda kilitli olan durumlar — Çizim yok'a dönmek hep serbest. */
export const ALAN_PLANI_KILITLI_DURUMLAR = [CIZIM_DURUM.CIZILIYOR, CIZIM_DURUM.TAMAMLANDI];

/**
 * Bu fuardaki salonun Alan Planı neden başlatılamıyor; null = başlatılabilir. `hall` getFuarHMax
 * → halls[i]. Backend (#235) her salona alanPlaniBaslatilabilir + alanPlaniEngeli koyar ve
 * PUT .../alan-plani-durumu aynı kuralla 409 döner; bu alanlar yoksa (backend henüz yeniden
 * başlatılmadı) aynı kural burada: fuar alanında kayıtlı olmayan salon (durum bilinmiyor) da
 * kilitli (Bilge, 2026-09-27).
 */
export function alanPlaniEngeli(hall) {
  if (!hall) return null;
  if (hall.alanPlaniBaslatilabilir === true) return null;
  if (typeof hall.alanPlaniEngeli === "string" && hall.alanPlaniEngeli) return hall.alanPlaniEngeli;
  if (hall.registeredAtFairground === false) {
    return i18n.t("components:salonDurumlari.salonBuFuarinFuarAlaninda");
  }
  if (hall.salonPlaniDurum !== CIZIM_DURUM.TAMAMLANDI) {
    return i18n.t("components:salonDurumlari.suAn", { ALAN_PLANI_ENGEL_KISA: i18n.t("components:salonDurumlari.onceSalonPlani"), label: cizimDurumMeta(hall.salonPlaniDurum ?? null).label });
  }
  return null;
}

/**
 * Bir planın çizim durumu rozeti. `onChange` verilirse tıklanınca üç seçenekli bir menü açar
 * (`extraItems` menünün başına eklenir — ör. "Alan Planına Git"); verilmezse salt okunur rozet.
 * `id` tooltip hedefi olarak sayfada benzersiz olmalı.
 * #236: `lockedDurums` menüde seçilemeyen durumlar, `lockedReason` nedenini menüde gösterir.
 */
export function CizimDurumuPill({
  id, plan, durum, tarih, onChange, disabled = false, busy = false, extraItems = null, showLabel = true,
  menuHeader = null, // #204: ör. "Çizim Durumunu Değiştir"; verilmezse "<Plan> — çizim durumu"
  lockedDurums = null, lockedReason = null,
}) {
  const { t } = useTranslation("components");
  const meta = cizimDurumMeta(durum);
  const { Icon } = meta;
  const planAdi = PLAN_ADLARI[plan] ?? plan;
  const tarihStr = fmtTarih(tarih);
  const tooltip = `${planAdi}: ${meta.label}${tarihStr ? ` (${tarihStr})` : ""}`;
  const badge = (
    <Badge
      id={id}
      color={meta.color}
      pill
      className={`d-inline-flex align-items-center gap-1 ${meta.className}`}
      style={{ cursor: onChange && !disabled ? "pointer" : "default", opacity: busy ? 0.6 : 1 }}
    >
      <Icon size={12} />
      {showLabel && <span>{meta.label}</span>}
    </Badge>
  );
  const tip = <UncontrolledTooltip target={id} placement="top">{tooltip}</UncontrolledTooltip>;

  if (!onChange || disabled) {
    return <>{badge}{tip}</>;
  }
  return (
    <UncontrolledDropdown className="d-inline-block">
      <DropdownToggle tag="span" data-toggle="dropdown">{badge}</DropdownToggle>
      <DropdownMenu>
        {extraItems}
        {extraItems && <DropdownItem divider />}
        <DropdownItem header>{menuHeader ?? t("salonDurumlari.cizimDurumu", { planAdi })}</DropdownItem>
        {lockedReason && lockedDurums?.length > 0 && (
          <DropdownItem text className="small text-warning" style={{ maxWidth: 280, whiteSpace: "normal" }}>
            {lockedReason}
          </DropdownItem>
        )}
        {CIZIM_DURUM_SECENEKLERI.map((o) => (
          <DropdownItem
            key={o.durum}
            active={o.durum === durum}
            disabled={busy || Boolean(lockedDurums?.includes(o.durum) && o.durum !== durum)}
            onClick={() => { if (o.durum !== durum) onChange(o.durum); }}
          >
            <o.Icon size={13} className="me-2" />{o.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
      {tip}
    </UncontrolledDropdown>
  );
}
